import React, { useEffect, useState } from 'react';
import { timetableAPI, masterAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiTrash2, FiClock, FiCopy, FiFilter } from 'react-icons/fi';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const TIME_SLOTS = [
  '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'
];

const Timetable = () => {
  const [timetable, setTimetable] = useState([]);
  const [classes, setClasses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Filters
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedBatch, setSelectedBatch] = useState('');
  const [selectedDay, setSelectedDay] = useState('');
  
  // Form data
  const [formData, setFormData] = useState({
    class_id: '',
    batch_id: '',
    day_of_week: '',
    period_number: '',
    start_time: '',
    end_time: '',
    subject_id: '',
    teacher_id: '',
    room_number: '',
    notes: '',
    status: 'active'
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedClass || selectedBatch || selectedDay) {
      fetchTimetable();
    }
  }, [selectedClass, selectedBatch, selectedDay]);

  const fetchInitialData = async () => {
    try {
      const [classesRes, subjectsRes, teachersRes] = await Promise.all([
        masterAPI.getClasses(),
        masterAPI.getSubjects(),
        masterAPI.getTeachers()
      ]);

      if (classesRes.data.success) setClasses(classesRes.data.data);
      if (subjectsRes.data.success) setSubjects(subjectsRes.data.data);
      if (teachersRes.data.success) setTeachers(teachersRes.data.data);
    } catch (error) {
      toast.error('Error loading initial data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBatches = async (classId) => {
    try {
      const response = await masterAPI.getBatches({ class_id: classId });
      if (response.data.success) {
        setBatches(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  };

  const fetchTimetable = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedClass) params.class_id = selectedClass;
      if (selectedBatch) params.batch_id = selectedBatch;
      if (selectedDay) params.day_of_week = selectedDay;

      const response = await timetableAPI.getTimetable(params);
      if (response.data.success) {
        setTimetable(response.data.data);
      }
    } catch (error) {
      toast.error('Error fetching timetable');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleClassChange = (classId) => {
    setSelectedClass(classId);
    setSelectedBatch('');
    setBatches([]);
    if (classId) {
      fetchBatches(classId);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      let response;
      if (editingItem) {
        response = await timetableAPI.updateTimetable(editingItem.id, formData);
      } else {
        response = await timetableAPI.createTimetable(formData);
      }

      if (response.data.success) {
        toast.success(response.data.message);
        setShowModal(false);
        setEditingItem(null);
        resetForm();
        fetchTimetable();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      class_id: item.class_id,
      batch_id: item.batch_id || '',
      day_of_week: item.day_of_week,
      period_number: item.period_number,
      start_time: item.start_time,
      end_time: item.end_time,
      subject_id: item.subject_id,
      teacher_id: item.teacher_id,
      room_number: item.room_number || '',
      notes: item.notes || '',
      status: item.status
    });
    if (item.class_id) {
      fetchBatches(item.class_id);
    }
    setShowModal(true);
  };

  const handleDelete = async (item) => {
    if (!window.confirm('Are you sure you want to delete this timetable entry?')) {
      return;
    }

    try {
      const response = await timetableAPI.deleteTimetable(item.id);
      if (response.data.success) {
        toast.success(response.data.message);
        fetchTimetable();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error deleting entry');
    }
  };

  const handleAddNew = () => {
    setEditingItem(null);
    resetForm();
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      class_id: '',
      batch_id: '',
      day_of_week: '',
      period_number: '',
      start_time: '',
      end_time: '',
      subject_id: '',
      teacher_id: '',
      room_number: '',
      notes: '',
      status: 'active'
    });
  };

  const renderWeeklyGrid = () => {
    if (!selectedClass) {
      return (
        <div className="text-center py-12">
          <FiFilter size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Select a Class</h3>
          <p className="text-gray-600">Please select a class to view the timetable</p>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-50">
              <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Day / Period</th>
              {[1, 2, 3, 4, 5, 6, 7, 8].map(period => (
                <th key={period} className="border border-gray-300 px-4 py-3 text-center font-semibold">
                  Period {period}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {DAYS.map(day => (
              <tr key={day}>
                <td className="border border-gray-300 px-4 py-3 font-semibold bg-gray-50">
                  {day}
                </td>
                {[1, 2, 3, 4, 5, 6, 7, 8].map(period => {
                  const entry = timetable.find(
                    t => t.day_of_week === day && t.period_number === period
                  );
                  
                  return (
                    <td key={period} className="border border-gray-300 px-2 py-2 text-sm">
                      {entry ? (
                        <div className="bg-blue-50 p-2 rounded hover:bg-blue-100 transition-colors">
                          <div className="font-semibold text-blue-900">{entry.subject_name}</div>
                          <div className="text-xs text-gray-600 mt-1">{entry.teacher_name}</div>
                          <div className="text-xs text-gray-500">{entry.start_time} - {entry.end_time}</div>
                          {entry.room_number && (
                            <div className="text-xs text-gray-500">Room: {entry.room_number}</div>
                          )}
                          <div className="flex gap-1 mt-2">
                            <button
                              onClick={() => handleEdit(entry)}
                              className="text-blue-600 hover:text-blue-800 p-1"
                              title="Edit"
                            >
                              <FiEdit2 size={14} />
                            </button>
                            <button
                              onClick={() => handleDelete(entry)}
                              className="text-red-600 hover:text-red-800 p-1"
                              title="Delete"
                            >
                              <FiTrash2 size={14} />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center text-gray-400 py-4">-</div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Timetable Management</h1>
          <p className="text-sm md:text-base text-gray-600 mt-1">Manage class schedules and periods</p>
        </div>
        <button
          onClick={handleAddNew}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <FiPlus /> Add Entry
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Class *</label>
            <select
              value={selectedClass}
              onChange={(e) => handleClassChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600"
            >
              <option value="">Select Class</option>
              {classes.map(cls => (
                <option key={cls.id} value={cls.id}>{cls.class}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Batch (Optional)</label>
            <select
              value={selectedBatch}
              onChange={(e) => setSelectedBatch(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600"
              disabled={!selectedClass}
            >
              <option value="">All Batches</option>
              {batches.map(batch => (
                <option key={batch.id} value={batch.id}>{batch.batch}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Day (Optional)</label>
            <select
              value={selectedDay}
              onChange={(e) => setSelectedDay(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600"
            >
              <option value="">All Days</option>
              {DAYS.map(day => (
                <option key={day} value={day}>{day}</option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={fetchTimetable}
              disabled={!selectedClass}
              className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              View Timetable
            </button>
          </div>
        </div>
      </div>

      {/* Timetable Grid */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading timetable...</p>
          </div>
        ) : (
          renderWeeklyGrid()
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {editingItem ? 'Edit Timetable Entry' : 'Add Timetable Entry'}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Class *</label>
                    <select
                      value={formData.class_id}
                      onChange={(e) => {
                        setFormData({ ...formData, class_id: e.target.value, batch_id: '' });
                        fetchBatches(e.target.value);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600"
                      required
                    >
                      <option value="">Select Class</option>
                      {classes.map(cls => (
                        <option key={cls.id} value={cls.id}>{cls.class}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Batch (Optional)</label>
                    <select
                      value={formData.batch_id}
                      onChange={(e) => setFormData({ ...formData, batch_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600"
                      disabled={!formData.class_id}
                    >
                      <option value="">All Batches</option>
                      {batches.map(batch => (
                        <option key={batch.id} value={batch.id}>{batch.batch}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Day *</label>
                    <select
                      value={formData.day_of_week}
                      onChange={(e) => setFormData({ ...formData, day_of_week: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600"
                      required
                    >
                      <option value="">Select Day</option>
                      {DAYS.map(day => (
                        <option key={day} value={day}>{day}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Period Number *</label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={formData.period_number}
                      onChange={(e) => setFormData({ ...formData, period_number: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Time *</label>
                    <input
                      type="time"
                      value={formData.start_time}
                      onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Time *</label>
                    <input
                      type="time"
                      value={formData.end_time}
                      onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Subject *</label>
                    <select
                      value={formData.subject_id}
                      onChange={(e) => setFormData({ ...formData, subject_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600"
                      required
                    >
                      <option value="">Select Subject</option>
                      {subjects.map(subject => (
                        <option key={subject.id} value={subject.id}>{subject.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Teacher *</label>
                    <select
                      value={formData.teacher_id}
                      onChange={(e) => setFormData({ ...formData, teacher_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600"
                      required
                    >
                      <option value="">Select Teacher</option>
                      {teachers.map(teacher => (
                        <option key={teacher.id} value={teacher.id}>{teacher.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Room Number</label>
                    <input
                      type="text"
                      value={formData.room_number}
                      onChange={(e) => setFormData({ ...formData, room_number: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600"
                      placeholder="e.g., Room 101"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status *</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600"
                      required
                    >
                      <option value="active">Active</option>
                      <option value="cancelled">Cancelled</option>
                      <option value="rescheduled">Rescheduled</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600"
                    rows="3"
                    placeholder="Additional notes..."
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-300"
                  >
                    {submitting ? 'Saving...' : (editingItem ? 'Update' : 'Create')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingItem(null);
                      resetForm();
                    }}
                    className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Timetable;
